import { useCallback, useMemo } from "react";
import { useQueryParams } from "./useQueryParams";
import {
  GLOBAL_FILTER_OPTIONS,
  DEFAULT_GLOBAL_FILTER,
  FILTER_KEYS,
  RESERVED_FILTER_KEYS,
  API_STATES_PARAM,
  sanitizeAdvancedFilters,
} from "../Configs/filters.config";
import { views } from "../Configs/config";
import { buildHospitalsUrl as buildHospitalsUrlPure } from "../utils/buildHospitalsUrl";

// useFilters - the single read/write interface for every filter in the app.
//
// All filter state lives in the URL query string; this hook derives typed,
// referentially-stable values from the URL and exposes setters that write back
// to it. Consuming components no longer read filters from Redux or receive them
// via props.
export function useFilters() {
  const { searchParams, setParams, updateSearchParams } = useQueryParams();

  // Raw values (primitives are naturally stable across re-renders) -----------
  const globalFilterValue =
    searchParams.get(FILTER_KEYS.GLOBAL_FILTER) || "all";
  const quickFilter = searchParams.get(FILTER_KEYS.QUICK_FILTER) || null;
  // Defaults to true; only persisted to the URL when toggled off.
  const partnerExclusiveFlag =
    searchParams.get(FILTER_KEYS.PARTNER_EXCLUSIVE) !== "false";
  const stateValue = searchParams.get(FILTER_KEYS.STATE) || "";
  const legendFilter = searchParams.get(FILTER_KEYS.LEGEND_FILTER) || null;
  const sortBy = searchParams.get(FILTER_KEYS.SORT_BY) || null;
  const sortOrder = searchParams.get(FILTER_KEYS.SORT_ORDER) || null;
  // The applied search term. Null when no search is active. (Named `searchTerm`
  // internally to avoid clashing with `search` below, which is the full query
  // string; it is exposed as `search` in the return value.)
  const searchTerm = searchParams.get(FILTER_KEYS.SEARCH) || null;
  // The active screen (map | table | compare). The URL is the single source of
  // truth; anything unknown/absent falls back to the map.
  const view = [views.MAP, views.TABLE, views.COMPARE].includes(
    searchParams.get(FILTER_KEYS.VIEW),
  )
    ? searchParams.get(FILTER_KEYS.VIEW)
    : views.MAP;
  const search = searchParams.toString();

  // Derived, referentially-stable values ------------------------------------

  // Resolve the bare URL value into the same { value, label } shape components
  // already consume, so the migration stays drop-in.
  const globalFilter = useMemo(
    () =>
      GLOBAL_FILTER_OPTIONS.find((o) => o.value === globalFilterValue) || {
        value: globalFilterValue,
        label: DEFAULT_GLOBAL_FILTER.label,
      },
    [globalFilterValue],
  );

  // Exposed as an array for consumer/API convenience, but the URL now only ever
  // holds a single selected state.
  const states = useMemo(
    () => (stateValue ? stateValue.split(",").filter(Boolean) : []),
    [stateValue],
  );

  // Advanced filters: any non-reserved param. The object is keyed by the
  // advanced form's own field names (e.g. `hospital_name`, `cbsa`,
  // `coc_accredited`) so existing consumers (HomePage card-counts) keep working.
  // The selected `state` is a reserved param, so it is not counted here.
  //
  // A stable serialization of *only* the advanced params is used as the memo
  // dependency so the resulting object keeps a stable reference when unrelated
  // params (sort, globalFilter, quickFilter, ...) change. This prevents
  // needless effect re-runs in consumers that depend on `filters`.
  const advancedDepKey = useMemo(() => {
    const sp = new URLSearchParams(search);
    const parts = [];
    for (const [key, value] of sp.entries()) {
      if (!RESERVED_FILTER_KEYS.has(key) && value !== "") {
        parts.push(`${key}\u0000${value}`);
      }
    }
    parts.sort();
    return parts.join("\u0001");
  }, [search]);

  const filters = useMemo(() => {
    const obj = {};
    if (!advancedDepKey) return obj;
    advancedDepKey.split("\u0001").forEach((pair) => {
      const sep = pair.indexOf("\u0000");
      obj[pair.slice(0, sep)] = pair.slice(sep + 1);
    });
    return obj;
  }, [advancedDepKey]);

  // A selected state counts as one applied filter (whether it came from the map
  // or the filter popup's State field), in addition to the advanced fields.
  const appliedFiltersCount = useMemo(
    () => Object.keys(filters).length + (stateValue ? 1 : 0),
    [filters, stateValue],
  );

  // Setters ------------------------------------------------------------------

  // Selecting a global filter only updates the globalFilter param. Advanced
  // filters (and the map/legend selection) are preserved so global + advanced
  // filters apply together.
  const setGlobalFilter = useCallback(
    (value) => {
      // Accept either a string value or a { value } option object.
      const next = typeof value === "object" && value ? value.value : value;
      setParams((params) => {
        if (!next || next === "all") params.delete(FILTER_KEYS.GLOBAL_FILTER);
        else params.set(FILTER_KEYS.GLOBAL_FILTER, next);
      });
    },
    [setParams],
  );

  const setQuickFilter = useCallback(
    (value) => {
      updateSearchParams({ [FILTER_KEYS.QUICK_FILTER]: value || "" });
    },
    [updateSearchParams],
  );

  const toggleQuickFilter = useCallback(
    (value) => {
      updateSearchParams({
        [FILTER_KEYS.QUICK_FILTER]: quickFilter === value ? "" : value,
      });
    },
    [updateSearchParams, quickFilter],
  );

  const setPartnerExclusive = useCallback(
    (flag) => {
      updateSearchParams({
        [FILTER_KEYS.PARTNER_EXCLUSIVE]: flag ? "" : "false",
      });
    },
    [updateSearchParams],
  );

  const togglePartnerExclusive = useCallback(() => {
    updateSearchParams({
      [FILTER_KEYS.PARTNER_EXCLUSIVE]: partnerExclusiveFlag ? "false" : "",
    });
  }, [updateSearchParams, partnerExclusiveFlag]);

  const setState = useCallback(
    (value) => {
      // Accept a string or an array (single selection); store one state.
      const next = Array.isArray(value) ? value.filter(Boolean)[0] : value;
      updateSearchParams({ [FILTER_KEYS.STATE]: next || "" });
    },
    [updateSearchParams],
  );

  const setLegendFilter = useCallback(
    (value) => {
      updateSearchParams({ [FILTER_KEYS.LEGEND_FILTER]: value || "" });
    },
    [updateSearchParams],
  );

  const setSearch = useCallback(
    (value) => {
      updateSearchParams({ [FILTER_KEYS.SEARCH]: value || "" });
    },
    [updateSearchParams],
  );

  const setSort = useCallback(
    (by, order) => {
      updateSearchParams({
        [FILTER_KEYS.SORT_BY]: by || "",
        [FILTER_KEYS.SORT_ORDER]: order || "",
      });
    },
    [updateSearchParams],
  );

  // Replace the entire advanced-filter set with a fresh applied object. Applying
  // advanced filters also clears the map/legend selection (legendFilter +
  // state), all in a single navigation. The advanced form's own `state` field
  // (when present) is re-applied below to the reserved `state` selection param.
  const setAdvancedFilters = useCallback(
    (filtersObj = {}, nextView) => {
      const sanitized = sanitizeAdvancedFilters(filtersObj);
      setParams((next) => {
        [...next.keys()].forEach((key) => {
          if (!RESERVED_FILTER_KEYS.has(key)) next.delete(key);
        });
        next.delete(FILTER_KEYS.LEGEND_FILTER);
        next.delete(FILTER_KEYS.STATE);
        // A text search can't be combined with advanced filters — applying
        // advanced filters clears any active search term so the results (and
        // the header title) reflect the filters only.
        next.delete(FILTER_KEYS.SEARCH);
        Object.entries(sanitized).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            next.set(key, String(value));
          }
        });
        // Switch screens in the SAME navigation. React Router v7 reads the
        // current params from a ref that only refreshes after render, so a
        // separate updateSearchParams({ view }) call would read a stale snapshot
        // and clobber the advanced filters we just wrote.
        if (nextView) next.set(FILTER_KEYS.VIEW, nextView);
      });
    },
    [setParams],
  );

  const clearAdvancedFilters = useCallback(() => {
    setParams((next) => {
      [...next.keys()].forEach((key) => {
        if (!RESERVED_FILTER_KEYS.has(key)) next.delete(key);
      });
    });
  }, [setParams]);

  // Clears map/legend selection (single hex click, legend bucket).
  const clearMapSelection = useCallback(() => {
    setParams((next) => {
      next.delete(FILTER_KEYS.STATE);
      next.delete(FILTER_KEYS.LEGEND_FILTER);
    });
  }, [setParams]);

  // The active view lives in the URL. This is the only way to change it, so
  // every screen switch is a single `updateSearchParams({ view })` write.
  const setViewParam = useCallback(
    (nextView) => {
      updateSearchParams({ [FILTER_KEYS.VIEW]: nextView });
    },
    [updateSearchParams],
  );

  // Clears advanced filters + map/legend selection in one navigation. Pass
  // `nextView` to switch screens in the same write (avoids a second, racing
  // URL update from a separate useSearchParams instance).
  const resetFilters = useCallback(
    (nextView) => {
      setParams((next) => {
        [...next.keys()].forEach((key) => {
          if (!RESERVED_FILTER_KEYS.has(key)) next.delete(key);
        });
        next.delete(FILTER_KEYS.STATE);
        next.delete(FILTER_KEYS.LEGEND_FILTER);
        // A full reset also clears any active search term.
        next.delete(FILTER_KEYS.SEARCH);
        if (nextView) next.set(FILTER_KEYS.VIEW, nextView);
      });
    },
    [setParams],
  );

  // Builds a backend query string from the current base filters, plus any
  // endpoint-specific extras. Mirrors the params the APIs already expect.
  const buildApiQuery = useCallback(
    (extra = {}, { includeStates = true } = {}) => {
      const params = new URLSearchParams();
      params.set(FILTER_KEYS.PARTNER_EXCLUSIVE, String(partnerExclusiveFlag));
      params.set(FILTER_KEYS.GLOBAL_FILTER, globalFilterValue);
      if (quickFilter) params.set(FILTER_KEYS.QUICK_FILTER, quickFilter);
      if (includeStates && states.length) {
        // Backend expects the plural `states` param.
        params.set(API_STATES_PARAM, states.join(","));
      }
      Object.entries(extra).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });
      return params.toString();
    },
    [partnerExclusiveFlag, globalFilterValue, quickFilter, states],
  );

  // Builds the hospitals backend URL (fetch or export) from the current
  // URL-derived filter context. Callers pass request-specific extras such as
  // `page`, `perPage`, `forExport`, `isBookmark`, or an override `states`
  // (e.g. legend-resolved states) / `search`. This is the single replacement
  // for the old localStorage `url` / `searchURL` side channel.
  const buildHospitalsUrl = useCallback(
    (extra = {}) =>
      buildHospitalsUrlPure({
        search: searchTerm,
        states,
        advancedFilters: filters,
        partnerExclusiveFlag,
        globalFilter: globalFilterValue,
        quickFilter,
        sortBy: sortBy || "ranking",
        sortOrder: sortOrder || "asc",
        ...extra,
      }),
    [
      searchTerm,
      states,
      filters,
      partnerExclusiveFlag,
      globalFilterValue,
      quickFilter,
      sortBy,
      sortOrder,
    ],
  );

  return {
    // values
    globalFilter,
    globalFilterValue,
    quickFilter,
    partnerExclusiveFlag,
    states,
    legendFilter,
    sortBy,
    sortOrder,
    view,
    search: searchTerm,
    filters,
    appliedFiltersCount,
    // setters
    setViewParam,
    updateSearchParams,
    setGlobalFilter,
    setQuickFilter,
    toggleQuickFilter,
    setPartnerExclusive,
    togglePartnerExclusive,
    setState,
    setLegendFilter,
    setSearch,
    setSort,
    setAdvancedFilters,
    clearAdvancedFilters,
    clearMapSelection,
    resetFilters,
    // helpers
    buildApiQuery,
    buildHospitalsUrl,
  };
}
